// src/pages/IndicesPrixConsommation.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import { useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CardMedia,
} from '@mui/material';
import { getPublications } from '../services/publicationService';

function IndicesPrixConsommation() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [year, setYear] = useState('');
  const [visibleReportsCount, setVisibleReportsCount] = useState(4);
  const [publicationsFr, setPublicationsFr] = useState([]);
  const [publicationsKi, setPublicationsKi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des deux types de publications
  useEffect(() => {
    setLoading(true);
    Promise.all([
      getPublications({ type: 'ipc_fr', perPage: 1000 }),
      getPublications({ type: 'ipc_ki', perPage: 1000 }),
    ])
      .then(([resFr, resKi]) => {
        if (resFr.error) {
          setError(resFr.error);
        } else if (resKi.error) {
          setError(resKi.error);
        } else {
          // Mapper les données françaises
          const mappedFr = resFr.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: item.date,
            publicationDate: item.publication_date,
          }));
          // Mapper les données kirundi
          const mappedKi = resKi.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: item.date,
            publicationDate: item.publication_date,
          }));
          setPublicationsFr(mappedFr);
          setPublicationsKi(mappedKi);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  // Récupérer les rapports selon l'onglet actif
  const currentReports = useMemo(() => {
    return activeTab === 0 ? publicationsFr : publicationsKi;
  }, [activeTab, publicationsFr, publicationsKi]);

  // Années disponibles pour la langue active
  const availableYears = useMemo(() => {
    const years = currentReports.map((r) => new Date(r.date).getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [currentReports]);

  // Filtrer par année
  const filteredReports = useMemo(() => {
    if (!year) return currentReports;
    return currentReports.filter((r) => new Date(r.date).getFullYear().toString() === year);
  }, [currentReports, year]);

  const handleShowMore = () => setVisibleReportsCount((prev) => prev + 2);
  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
    setYear('');         // réinitialiser le filtre année
    setVisibleReportsCount(4);
  };
  const handleYearChange = (e) => setYear(e.target.value);

  const allReportsLoaded = visibleReportsCount >= filteredReports.length;

  // États de chargement / erreur
  if (loading) {
    return (
      <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
        <NavigationBar />
        <br />
        <Container maxWidth="lg">
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Indices des Prix à la Consommation
          </Typography>
          <Typography>Chargement des données...</Typography>
        </Container>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
        <NavigationBar />
        <br />
        <Container maxWidth="lg">
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Indices des Prix à la Consommation
          </Typography>
          <Typography color="error">{error}</Typography>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
      <NavigationBar />
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        <br />
      </Typography>
      <Container maxWidth="lg">
        {/* Onglets */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            textColor="inherit"
            indicatorColor="secondary"
          >
            <Tab label="FRANCAIS" />
            <Tab label="KIRUNDI" />
          </Tabs>
        </div>

        {/* Contenu FRANCAIS */}
        {activeTab === 0 && (
          <div>
            <Typography
              variant="h5"
              align="justify"
              gutterBottom
              style={{ color: '#2E7D32' }}
            >
              Indices des Prix à la Consommation
            </Typography>
            <Typography variant="body1" align="justify" gutterBottom>
              L’Indice des Prix à la Consommation (IPC) fait actuellement référence à la base 100 de 2016/2017.
              Il couvre tout le territoire national tant en milieu urbain que rural. Les pondérations dérivent de
              l’enquête sur les conditions de vie des ménages (ECVMB2013/2014-Volet dépenses). L’IPC montre les
              données sur la variation des prix des biens et services et permet d’analyser l’inflation et son
              impact sur le pouvoir d’achat des ménages au fil du temps.
            </Typography>

            <FormControl variant="outlined" style={{ marginLeft: '0px', width: '200px' }}>
              <Select value={year} onChange={handleYearChange} displayEmpty>
                <MenuItem value="">
                  <em>Tous les indices</em>
                </MenuItem>
                {availableYears.map((y) => (
                  <MenuItem key={y} value={y.toString()}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={1} style={{ marginTop: '10px' }}>
              {filteredReports.slice(0, visibleReportsCount).map((report, index) => (
                <Grid item xs={12} sm={6} md={12} key={index} style={{ marginBottom: '5px' }}>
                  <Card
                    elevation={3}
                    style={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      display: 'flex',
                    }}
                  >
                    <CardMedia
                      component="img"
                      sx={{ width: 61, height: 90, m: 2, cursor: 'pointer' }}
                      image={report.image}
                      alt={report.title}
                      onClick={() => window.open(report.link, '_blank')}
                    />
                    <CardContent style={{ flex: 1, padding: '20px' }}>
                      <Typography
                        variant="h6"
                        component="div"
                        style={{
                          fontWeight: 'bold',
                          color: 'green',
                          marginBottom: '10px',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(report.link, '_blank')}
                      >
                        <DownloadIcon /> {report.title}
                      </Typography>
                      <Typography variant="body2" component="div" style={{ marginBottom: '20px' }}>
                        {report.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {!allReportsLoaded && (
              <Button
                onClick={handleShowMore}
                style={{ marginTop: '20px', backgroundColor: '#2E7D32', color: '#FFFFFF' }}
              >
                Plus d'indice
              </Button>
            )}

            {/* NOTE EN FRANCAIS */}
            <Typography
              variant="body1"
              align="justify"
              paragraph
              style={{ marginTop: '24px', lineHeight: 1.6 }}
            >
              <strong>Note :</strong> Les indices des Prix à la Consommation (IPC) sont publiés le{' '}
              <strong>15 de chaque mois</strong>, soit 15 jours après la clôture du mois précédent.
              <br />
              <br />
              Voici le calendrier des prochaines publications des indices :
              <ul
                style={{
                  listStyleType: 'circle',
                  margin: '10px 0 10px 20px',
                  padding: 0,
                }}
              >
                <li>
                  <strong>15 janvier</strong> - Publication des indices du mois de décembre
                </li>
                <li>
                  <strong>15 février</strong> - Publication des indices du mois de janvier
                </li>
                <li>
                  <strong>15 mars</strong> - Publication des indices du mois de février
                </li>
                <li>
                  <strong>15 avril</strong> - Publication des indices du mois de mars
                </li>
                <li>
                  <strong>15 mai</strong> - Publication des indices du mois d'avril
                </li>
                <li>
                  <strong>15 juin</strong> - Publication des indices du mois de mai
                </li>
                <li>
                  <strong>15 juillet</strong> - Publication des indices du mois de juin
                </li>
                <li>
                  <strong>15 août</strong> - Publication des indices du mois de juillet
                </li>
                <li>
                  <strong>15 septembre</strong> - Publication des indices du mois d'août
                </li>
                <li>
                  <strong>15 octobre</strong> - Publication des indices du mois de septembre
                </li>
                <li>
                  <strong>15 novembre</strong> - Publication des indices du mois d'octobre
                </li>
                <li>
                  <strong>15 décembre</strong> - Publication des indices du mois de novembre
                </li>
              </ul>
            </Typography>

            <Button
              onClick={() => navigate('/indice')}
              style={{ marginTop: '20px', backgroundColor: '#2E7D32', color: '#FFFFFF' }}
            >
              <ArrowBackIcon style={{ marginRight: '8px' }} />
              Retour à la page des indices
            </Button>
          </div>
        )}

        {/* Contenu KIRUNDI */}
        {activeTab === 1 && (
          <div>
            <Typography
              variant="h5"
              align="justify"
              gutterBottom
              style={{ color: '#2E7D32' }}
            >
              Igipimo c’ihindagurika ry’ibiciro
            </Typography>
            <Typography variant="body1" align="justify" gutterBottom>
              Iki kigabane gitanga raporo y'Ibiciro (IPC) mu Burundi, kikerekana itandukaniro riri hagati
              y'ibiciro vy'ibintu n'ibindi bikorwa. Aya makuru afasha mu gusesangura ukwiyongera kw'ibiciro
              hamwe n'ingaruka zavyo ku bushobozi bwo kugura.
            </Typography>

            <FormControl variant="outlined" style={{ marginLeft: '0px', width: '200px' }}>
              <Select value={year} onChange={handleYearChange} displayEmpty>
                <MenuItem value="">
                  <em>Ibipimo vyose</em>
                </MenuItem>
                {availableYears.map((y) => (
                  <MenuItem key={y} value={y.toString()}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={1} style={{ marginTop: '10px' }}>
              {filteredReports.slice(0, visibleReportsCount).map((report, index) => (
                <Grid item xs={12} sm={6} md={12} key={index} style={{ marginBottom: '5px' }}>
                  <Card
                    elevation={3}
                    style={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      display: 'flex',
                    }}
                  >
                    <CardMedia
                      component="img"
                      sx={{ width: 91, height: 140, m: 2, cursor: 'pointer' }}
                      image={report.image}
                      alt={report.title}
                      onClick={() => window.open(report.link, '_blank')}
                    />
                    <CardContent style={{ flex: 1, padding: '20px' }}>
                      <Typography
                        variant="h6"
                        component="div"
                        style={{
                          fontWeight: 'bold',
                          color: 'green',
                          marginBottom: '10px',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(report.link, '_blank')}
                      >
                        <DownloadIcon /> {report.title}
                      </Typography>
                      <Typography variant="body2" component="div" style={{ marginBottom: '20px' }}>
                        {report.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {!allReportsLoaded && (
              <Button
                onClick={handleShowMore}
                style={{ marginTop: '20px', backgroundColor: '#2E7D32', color: '#FFFFFF' }}
              >
                Ibindi bipimo
              </Button>
            )}

            {/* NOTE EN KIRUNDI */}
            <Typography
              variant="body1"
              align="justify"
              paragraph
              style={{ marginTop: '24px', lineHeight: 1.6 }}
            >
              <strong>Menya neza :</strong> Igipimo cihindagurika ry'Ibiciro(IPC) cama gisohoka kuwa{' '}
              <strong>15 buri kwezi</strong>, canke imisi 15 inyuma y'ukwezi kurangiye. Iki kirangamisi
              cemeza ko amakuru akenewe yamaho, bigafasha mu gukurikirana iterambere ry'ibiciro muri urwo rwego.
              <br />
              <br />
              Raba ikirangamisi c'aho ibipimo bizosohokera mu gihe c'imbere :
              <ul
                style={{
                  listStyleType: 'circle',
                  margin: '10px 0 10px 20px',
                  padding: 0,
                }}
              >
                <li>
                  <strong>15 Nzero</strong> - Gusohora Igipimo c'ukwezi kwa Kigarama
                </li>
                <li>
                  <strong>15 Ruhuhuma</strong> - Gusohora Igipimo c'ukwezi kwa Nzero
                </li>
                <li>
                  <strong>15 Ntwarante</strong> - Gusohora Igipimo c'ukwezi kwa Ruhuhuma
                </li>
                <li>
                  <strong>15 Ndamukiza</strong> - Gusohora Igipimo c'ukwezi kwa Ntwarante
                </li>
                <li>
                  <strong>15 Rusama</strong> - Gusohora Igipimo c'ukwezi kwa Ndamukiza
                </li>
                <li>
                  <strong>15 Rusama</strong> - Gusohora Igipimo c'ukwezi kwa Rusama
                </li>
                <li>
                  <strong>15 Mukakaro</strong> - Gusohora Igipimo c'ukwezi kwa Kamena
                </li>
                <li>
                  <strong>15 Mukakaro</strong> - Gusohora Igipimo c'ukwezi kwa Mukakaro
                </li>
                <li>
                  <strong>15 Nyakanga</strong> - Gusohora Igipimo c'ukwezi kwa Mukakaro
                </li>
                <li>
                  <strong>15 Gitugutu</strong> - Gusohora Igipimo c'ukwezi kwa Nyakanga
                </li>
                <li>
                  <strong>15 Munyonyo</strong> - Gusohora Igipimo c'ukwezi kwa Gitugutu
                </li>
                <li>
                  <strong>15 Kigarama</strong> - Gusohora Igipimo c'ukwezi kwa Munyonyo
                </li>
              </ul>
            </Typography>

            <Button
              onClick={() => navigate('/indice')}
              style={{ marginTop: '20px', backgroundColor: '#2E7D32', color: '#FFFFFF' }}
            >
              <ArrowBackIcon style={{ marginRight: '8px' }} />
              Subira kuri page y'ibipimo
            </Button>
          </div>
        )}
      </Container>
      <br />
      <br />
      <Footer />
    </div>
  );
}

export default IndicesPrixConsommation;